/**
 * InfiniteScrollSection Component
 *
 * Handles infinite scroll sentinel and optional "Load More" button.
 *
 * @module components/ui/PaginatedList/components/InfiniteScrollSection
 */

import { memo, useEffect } from 'react';
import { Button, Center, Loader, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import styles from '../PaginatedList.module.css';

interface InfiniteScrollSectionProps {
  /** Whether there are more items to load */
  hasNextPage: boolean;
  /** Whether currently loading */
  isLoading: boolean;
  /** Show button instead of auto-scroll */
  showButton?: boolean;
  /** Callback to load more items */
  onLoadMore: () => void;
  /** Intersection observer threshold */
  threshold?: number;
  /**
   * Scroll container the sentinel is observed within. Defaults to `undefined`
   * (the browser uses the page viewport). Pass the scrollable parent when the
   * sentinel lives inside an internally-scrolling container (e.g. a portaled
   * dropdown) so intersection fires on that container's scroll, not the page.
   */
  root?: Element | null;
}

function InfiniteScrollSectionInner({
  hasNextPage,
  isLoading,
  showButton = false,
  onLoadMore,
  threshold = 0.1,
  root,
}: InfiniteScrollSectionProps) {
  const { t } = useTranslation();

  // Intersection observer for automatic loading
  const { isIntersecting, ref: intersectionRef } = useIntersectionObserver({
    root,
    threshold,
    rootMargin: '100px',
  });

  // Auto-load when sentinel visible (only if not using button mode)
  useEffect(() => {
    if (!showButton && isIntersecting && hasNextPage && !isLoading) {
      onLoadMore();
    }
  }, [showButton, isIntersecting, hasNextPage, isLoading, onLoadMore]);

  // Nothing to show if no more pages and not loading
  if (!hasNextPage && !isLoading) {
    return null;
  }

  // Button mode
  if (showButton) {
    return (
      <div className={styles.loadMoreContainer}>
        <Button
          variant="light"
          onClick={onLoadMore}
          loading={isLoading}
          disabled={isLoading || !hasNextPage}
          className={styles.loadMoreButton}
        >
          {isLoading
            ? t('common.pagination.loading')
            : t('common.pagination.loadMore')}
        </Button>
      </div>
    );
  }

  // Auto-scroll mode with sentinel
  return (
    <div ref={intersectionRef} className={styles.sentinel}>
      {isLoading && (
        <Center className={styles.loader}>
          <Loader size="sm" />
        </Center>
      )}
      {!isLoading && hasNextPage && (
        <Text c="dimmed" size="xs" ta="center" className={styles.scrollHint}>
          {t('common.scrollToLoadMore')}
        </Text>
      )}
    </div>
  );
}

export const InfiniteScrollSection = memo(InfiniteScrollSectionInner);
